import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../../components/context/LoginContext/AuthContext";

/**
 * @typedef {Object} institution
 * @property {number} id
 * @property {string} name
 * @property {string|null} image_url
 */

/**
 * @typedef {Object} InvoicePayments
 * @typedef {Object} institution
 * @property {number} id
 * @property {string} tenant_id
 * @property {number} institution_id
 * @property {number} invoice_id
 * @property {string} amount
 * @property {number} source
 * @property {number} method
 * @property {string} status
 */

/**
 * Admin - Get All School Agreement Invoices (with filter & pagination)
 */
export const useGetAllInvoicePayments = ({
  status = "all",
  page = 1,
  perPage = 10,
} = {}) => {
  const { user, fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  /** @type {[InvoicePayments[], Function]} */
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInvoicePayments = useCallback(
    async (customParams = {}) => {
      if (!user?.token) return;

      const finalStatus = customParams.status ?? status;
      const finalPage = customParams.page ?? page;
      const finalPerPage = customParams.perPage ?? perPage;

      const query = new URLSearchParams();
      if (finalStatus && finalStatus !== "all") {
        query.append("filter[status]", finalStatus);
      }
      query.append("page", finalPage);
      query.append("per_page", finalPerPage);

      setLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `${apiUrl}/school/payments?${query.toString()}`,
          { method: "GET" }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch invoices");
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || "Failed to fetch invoices");
        }

        setInvoicePayments(result.data || []);
        setMeta(result.meta || null);
        setStats(result.stats || null);
      } catch (err) {
        console.error("Invoices Fetch Error:", err);
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, fetchWithAuth, user, status, page, perPage]
  );

  useEffect(() => {
    fetchInvoicePayments();
  }, [fetchInvoicePayments]);

  return {
    invoicePayments,
    meta,
    loading,
    stats,
    error,
    refetch: fetchInvoicePayments, // can pass {status, page, perPage}
  };
};
